import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/ui/cn';
import { Icon } from '@/shared/ui/Icon';

export type PinPadProps = {
  onDigit: (digit: string) => void;
  onErase: () => void;
  /** Saisie fermée : envoi en cours, ou code bloqué après trop d'essais. */
  disabled?: boolean;
};

const ROWS: ReadonlyArray<ReadonlyArray<string>> = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', 'erase'],
];

const KEY =
  'flex size-16 items-center justify-center rounded-pill text-fg transition-colors hover:bg-sunken active:bg-fill disabled:pointer-events-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

/**
 * Clavier du code de confidentialité.
 *
 * Un clavier dessiné, comme sur le mobile : il ne propose rien, ne retient
 * rien, et tient toujours à la même place. Sur ordinateur, les chiffres du
 * clavier physique font la même chose (la fenêtre les écoute). Dix chiffres et
 * un effacement, sans couleur : le seul élément actif est la rangée de points.
 */
export function PinPad({ onDigit, onErase, disabled = false }: PinPadProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('mx-auto flex flex-col gap-3', disabled && 'opacity-35')}>
      {ROWS.map((row) => (
        <div key={row.join('')} className="flex gap-10">
          {row.map((key, index) => {
            if (key === '') {
              // La case vide tient la grille : le zéro reste sous le huit.
              return <span key={`blank-${index}`} className="size-16" aria-hidden />;
            }
            if (key === 'erase') {
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={t('pin.erase')}
                  disabled={disabled}
                  onClick={onErase}
                  className={KEY}
                >
                  <Icon name="backspace" size={24} />
                </button>
              );
            }
            return (
              <button
                key={key}
                type="button"
                aria-label={t('pin.digit', { digit: key })}
                disabled={disabled}
                onClick={() => onDigit(key)}
                className={cn(KEY, 'text-display tabular')}
              >
                {key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
